import React     from 'react';
import { parse } from 'query-string';

import { NoAuthRoutes }     from './no-auth-routes';
import { LoggedInTemplate } from '../logged-in-template/logged-in-template';
import { Spinner }          from '../common/spinner/spinner';
import { useGlobalCtx }     from '../../context/ctx-hook';
import { setCtxField }      from '../../context/ctx-actions';
import { parseCookies }     from '../../helpers/pure-functions';
import { exchangeRefreshToken, startTokenRefreshTimer } from '../../helpers/token-refresh';
import { ROUTES }           from '../../helpers/constants/constants';


function RoutingPaths() {
  const {state: {isLoggedIn, pageStatus}} = useGlobalCtx();
  const { globalDispatch } = useGlobalCtx();
  const [isRefreshingToken, setIsRefreshingToken] = React.useState(false);

  const COOKIE   = parseCookies(document.cookie);
  const QS_PARMS = parse(window.location.search, {parseNumbers: true});
  const SIGN_OUT = (COOKIE?.qkn_signout || QS_PARMS.signout) ?? null;
  const LS_BEARER = localStorage.getItem('bearer');

  // Check for refresh token in URL first (preferred method for inter-app handoff)
  const URL_REFRESH_TOKEN = QS_PARMS.refreshToken ?? null;

  // This is a hotfix for broken URL we are recieveing in embeded flows.
  // This will handle multiple bearer tokens in the URL
  // Only use bearer from URL if no refresh token is present (backward compatibility)
  const searchParams   = new URLSearchParams(window.location.search);
  const tokens         = searchParams.getAll('bearer');
  const nonEmptyTokens = tokens.filter(e => e);
  const URL_BEARER     = nonEmptyTokens[0] ?? null;

  // Prioritize: 1) refreshToken in URL, 2) bearer in cookie, 3) bearer in URL
  // If refreshToken is present, we'll exchange it and ignore any bearer in URL
  const BEARER = URL_REFRESH_TOKEN ? null : (COOKIE?.qkn_bearer || URL_BEARER) ?? null;

  // Handle refresh token from URL (takes priority over bearer token)
  React.useEffect(() => {
    if (URL_REFRESH_TOKEN && !localStorage.getItem('bearer') && !isRefreshingToken) {
      setIsRefreshingToken(true);

      exchangeRefreshToken(URL_REFRESH_TOKEN)
        .then(() => {
          globalDispatch(setCtxField('isLoggedIn', true));

          // Clear refreshToken and any bearer from URL while preserving other params
          const url = new URL(window.location.href);
          url.searchParams.delete('refreshToken');
          url.searchParams.delete('bearer');
          window.history.replaceState({}, '', url.toString());

          setIsRefreshingToken(false);
        })
        .catch((error) => {
          console.error('Failed to exchange refresh token:', error);
          setIsRefreshingToken(false);
          // Redirect to login on failure
          window.location.href = ROUTES.LOGIN;
        });
    }
  }, [URL_REFRESH_TOKEN, isRefreshingToken, globalDispatch]);

  // Start refresh timer if we have a valid token on mount
  React.useEffect(() => {
    if (localStorage.getItem('bearer') && localStorage.getItem('tokenExpiration')) {
      startTokenRefreshTimer();
    }
  }, []);

  if(!BEARER && LS_BEARER ) {
    localStorage.removeItem('bearer');
    globalDispatch(setCtxField('isLoggedIn', false));
  }

  if (SIGN_OUT) {
    localStorage.setItem('signout', SIGN_OUT);
  }

  // Only store bearer from URL/cookie if we're not using a refresh token
  if (BEARER) {
    localStorage.setItem('bearer', BEARER);
  }

  // Show spinner while exchanging refresh token
  if (isRefreshingToken) {
    return <Spinner />;
  }

  return isLoggedIn || localStorage.getItem('bearer')
    ? <LoggedInTemplate status={pageStatus}/>
    : <NoAuthRoutes/>;
}

RoutingPaths.PageLayout = 'RoutingPaths';

export { RoutingPaths };
